import { useMutation, useSuspenseQuery } from '@apollo/client';
import React, { useMemo, useState } from 'react';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import {
  Badge,
  ColDefBuilder,
  DataTable,
  Heading,
  Icon,
  LinkButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SimpleTooltip,
  Stack,
  Switch,
  useToast,
} from '@eluve/components';
import { ResultOf } from '@eluve/graphql.tada';

import { BundleActiveToggle } from './BundleActiveToggle';
import { DisableBundleConfirmationDialog } from './DisableBundleConfirmationDialog';
import { GlobalDefaultBundleSelect } from './GlobalDefaultBundleSelect';
import {
  getAllTemplateBundlesQuery,
  getTemplateBundleDisplayName,
  templateBundleListItemFragment,
  updateTemplateBundleIsActiveMutation,
  updateTemplateBundleIsDefaultForNewTenantsMutation,
} from './template-bundle.operations';
import { useDisableBundleFlow } from './useDisableBundleFlow';

type BundleListEntity = ResultOf<typeof templateBundleListItemFragment>;

type TemplateBundleRow = {
  id: string;
  displayName: string;
  promptTemplateName: string;
  modelName: string;
  outputTemplateName: string;
  isActive: boolean;
  modelArgsActive: boolean;
  isDefaultForNewTenants: boolean;
  tenantCount: number;
  createdAt: string;
  /** Owning org name for tenant-specific bundles, 'Global' otherwise */
  scope: string;
  isTenantOwned: boolean;
  bundleListEntity: BundleListEntity;
};

type ScopeFilter = 'all' | 'global' | 'tenant';

export const TemplateBundlesListPage: React.FC = () => {
  const toast = useToast();

  const [toggleActive] = useMutation(updateTemplateBundleIsActiveMutation, {
    context: eluveAdminHasuraContext,
    onError: (error) => {
      toast.error(`Failed to update bundle: ${error.message}`);
    },
  });

  const [toggleDefaultForNewTenants] = useMutation(
    updateTemplateBundleIsDefaultForNewTenantsMutation,
    {
      context: eluveAdminHasuraContext,
      onError: (error) => {
        toast.error(`Failed to update bundle: ${error.message}`);
      },
    },
  );

  const { requestDisable, dialogProps } = useDisableBundleFlow({
    mode: 'global',
  });

  const columns = useMemo(
    () =>
      new ColDefBuilder<TemplateBundleRow>()
        .linkSortable('displayName', (row) => `./${row.id}`, 'Name')
        .defaultSortable('scope', {
          label: 'Scope',
          cellRenderer: (row) => (
            <Badge variant={row.isTenantOwned ? 'outline' : 'secondary'}>
              {row.scope}
            </Badge>
          ),
        })
        .defaultSortable('promptTemplateName', 'Prompt Template')
        .defaultSortable('modelName', 'Model')
        .defaultSortable('outputTemplateName', 'Output Template')
        .defaultSortable('isActive', {
          label: 'Active',
          cellRenderer: (row) => (
            <BundleActiveToggle
              isActive={row.isActive}
              modelArgsActive={row.modelArgsActive}
              onActivate={() =>
                toggleActive({
                  variables: { id: row.id, isActive: true },
                  optimisticResponse: {
                    updateLlmModelPromptTemplatesByPk: {
                      ...row.bundleListEntity,
                      isActive: true,
                    },
                  },
                })
              }
              onDeactivate={() =>
                requestDisable(row.id, row.displayName, {
                  wasDefaultForNewTenants: row.isDefaultForNewTenants,
                })
              }
            />
          ),
        })
        .defaultSortable('isDefaultForNewTenants', {
          label: 'Default for new tenants',
          cellRenderer: (row) => (
            <SimpleTooltip
              label={
                row.isTenantOwned
                  ? "This bundle belongs to a specific organization, so it can't be a default for new tenants."
                  : undefined
              }
            >
              <span className="inline-flex">
                <Switch
                  size="sm"
                  // Tenant-owned bundles are private to one org; the DB enforces
                  // this too via the global-scope guard.
                  disabled={!row.isActive || row.isTenantOwned}
                  checked={row.isDefaultForNewTenants}
                  onCheckedChange={(checked) => {
                    toggleDefaultForNewTenants({
                      variables: {
                        id: row.id,
                        isDefaultForNewTenants: checked,
                      },
                      optimisticResponse: {
                        updateLlmModelPromptTemplatesByPk: {
                          ...row.bundleListEntity,
                          isDefaultForNewTenants: checked,
                        },
                      },
                    });
                  }}
                />
              </span>
            </SimpleTooltip>
          ),
        })
        .defaultSortable('tenantCount', 'Tenants')
        .dateSortable('createdAt', 'Created At')
        .build(),
    [toggleActive, toggleDefaultForNewTenants, requestDisable],
  );
  const {
    data: { llmModelPromptTemplates },
  } = useSuspenseQuery(getAllTemplateBundlesQuery, {
    context: eluveAdminHasuraContext,
  });

  const rows = useMemo(() => {
    return llmModelPromptTemplates.map<TemplateBundleRow>((bundle) => {
      const promptName = bundle.template?.name ?? 'Unknown Prompt';
      const modelName =
        bundle.model_args?.name ??
        bundle.model_args?.modelType ??
        'Unknown Model';
      const outputName = bundle.output_template?.name ?? 'None';
      const isTenantOwned = Boolean(bundle.output_template?.tenantId);

      return {
        id: bundle.id,
        displayName: getTemplateBundleDisplayName(bundle),
        promptTemplateName: promptName,
        modelName,
        outputTemplateName: outputName,
        isActive: bundle.isActive,
        modelArgsActive: bundle.model_args?.isActive ?? true,
        isDefaultForNewTenants: bundle.isDefaultForNewTenants,
        tenantCount:
          bundle.tenantTemplateBundlesAggregate?.aggregate?.count ?? 0,
        createdAt: bundle.createdAt,
        scope: isTenantOwned
          ? (bundle.output_template?.tenant?.name ?? 'Tenant')
          : 'Global',
        isTenantOwned,
        bundleListEntity: bundle,
      };
    });
  }, [llmModelPromptTemplates]);

  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');
  const filteredRows = useMemo(() => {
    if (scopeFilter === 'global') {
      return rows.filter((row) => !row.isTenantOwned);
    }
    if (scopeFilter === 'tenant') {
      return rows.filter((row) => row.isTenantOwned);
    }
    return rows;
  }, [rows, scopeFilter]);

  // The global default can only point at an active bundle (Step 9 keeps it
  // active while it holds the role, so the current selection always appears).
  const activeBundleOptions = useMemo(
    () =>
      llmModelPromptTemplates
        // A tenant-owned bundle must never become the system-wide default.
        .filter(
          (bundle) => bundle.isActive && !bundle.output_template?.tenantId,
        )
        .map((bundle) => ({
          id: bundle.id,
          displayName: getTemplateBundleDisplayName(bundle),
        })),
    [llmModelPromptTemplates],
  );

  return (
    <>
      <Stack justify="between" gap={2} direction="horizontal">
        <div className="flex-1">
          <div className="my-2 py-2">
            <Heading>Template Bundles</Heading>
          </div>
        </div>
        <LinkButton to="create">
          <Icon name="Plus" size="auto" />
          Create Template Bundle
        </LinkButton>
      </Stack>
      <div className="my-4">
        <GlobalDefaultBundleSelect options={activeBundleOptions} />
      </div>
      <div className="my-2 w-56">
        <Select
          value={scopeFilter}
          onValueChange={(value) => setScopeFilter(value as ScopeFilter)}
        >
          <SelectTrigger aria-label="Filter by scope">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All scopes</SelectItem>
            <SelectItem value="global">Global only</SelectItem>
            <SelectItem value="tenant">Tenant-owned only</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <DataTable
          columns={columns}
          data={filteredRows}
          enableGlobalSearch
          initialSortingState={[{ id: 'createdAt', desc: true }]}
          autoResetPageIndex={false}
        />
      </div>

      <DisableBundleConfirmationDialog {...dialogProps} />
    </>
  );
};
